// ============================================================================
//  EmbedSnippetSection — bloco "Incorpore em seu site"
//
//  Section reutilizavel pelas 5 landings que tem variante /embed/*
//  (3 simuladores + 2 lineup builders). Mostra um <textarea readonly> com
//  o snippet de <iframe> + botao "Copiar" pra clipboard.
//
//  Padrao da industria (SofaScore, FlashScore, Kickbase): snippet pronto
//  pra copiar/colar em blogs/podcasts/portais — gera backlinks naturais.
//
//  Visual: gradient roxo (contrasta com sections light vizinhas), glass
//  card, mobile-first padding/fonts.
// ============================================================================

import { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Check, Copy, Code2, Sparkles } from 'lucide-react';

const SITE_URL = 'https://bolaoelite.com.br';

/** Mapeia variantes de `i18n.language` (pt-BR, en-US, es-ES) pro código curto. */
function shortLang(lng: string | undefined): 'pt-BR' | 'en' | 'es' {
  const l = (lng ?? 'pt-BR').toLowerCase();
  if (l.startsWith('en')) return 'en';
  if (l.startsWith('es')) return 'es';
  return 'pt-BR';
}

interface EmbedSnippetSectionProps {
  /** Slug da rota /embed/<slug> (ex.: "simulador-copa-do-mundo-2026") */
  embedSlug: string;
  /** Texto humano descritivo do widget (ex.: "Simulador da Copa do Mundo 2026") */
  widgetLabel: string;
  /** Altura sugerida do iframe (default 900px — height fixo com scroll interno) */
  height?: number;
}

export function EmbedSnippetSection({
  embedSlug,
  widgetLabel,
  height = 900,
}: EmbedSnippetSectionProps) {
  const { t, i18n } = useTranslation('landing');
  const [copied, setCopied] = useState(false);

  // Idioma corrente da landing → ?lang= no iframe pra que o widget abra no
  // mesmo idioma quando colado num blog/portal em outro domínio. Sem isso,
  // o detector i18next dentro do iframe caía no cookie/navegador do site
  // de quem incorpora (geralmente PT). O detector da web inclui
  // `querystring` na ordem de detecção (lookupQuerystring='lang').
  const lang = shortLang(i18n.language);
  const langSuffix = lang === 'pt-BR' ? '' : `?lang=${lang}`;

  const snippet = `<iframe src="${SITE_URL}/embed/${embedSlug}${langSuffix}" width="100%" height="${height}" frameborder="0" style="border:0;max-width:1200px" title="${widgetLabel}"></iframe>`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback: seleciona o texto pra usuario copiar manualmente
      const ta = document.getElementById(`embed-snippet-${embedSlug}`) as HTMLTextAreaElement | null;
      ta?.select();
    }
  };

  return (
    <section
      id="incorporar"
      className="relative overflow-hidden py-12 sm:py-16 px-4 scroll-mt-16 bg-gradient-to-br from-purple-900 via-slate-900 to-slate-950"
    >
      {/* Glow decorativo */}
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 20% 0%, rgba(168, 85, 247, 0.4) 0%, transparent 50%), radial-gradient(circle at 80% 100%, rgba(168, 85, 247, 0.3) 0%, transparent 50%)',
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-4 rounded-full bg-purple-500/20 border border-purple-400/30 backdrop-blur-sm">
            <Sparkles className="w-3.5 h-3.5 text-purple-300" />
            <span className="text-xs font-bold text-purple-200 uppercase tracking-wider">
              {t('embed.badge', 'Grátis · Sem cadastro')}
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white mb-3 leading-tight">
            {t('embed.title', 'Incorpore em seu site')}
          </h2>
          <p className="text-purple-100/80 text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
            {t('embed.desc', 'Tem um blog esportivo, podcast ou portal? Copie o snippet abaixo e cole no HTML — o {{widget}} fica disponível direto na sua página.', { widget: widgetLabel.toLowerCase() })}
          </p>
        </div>

        {/* Card glass com snippet */}
        <div className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur-md shadow-2xl overflow-hidden">
          {/* Barra superior estilo terminal */}
          <div className="flex items-center justify-between px-3 sm:px-4 py-2.5 bg-black/30 border-b border-white/10">
            <div className="flex items-center gap-2">
              <Code2 className="w-4 h-4 text-purple-300" />
              <span className="text-xs font-mono font-semibold text-purple-200">
                snippet.html
              </span>
            </div>
            <button
              type="button"
              onClick={handleCopy}
              aria-label={t('embed.copy_aria', 'Copiar snippet')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg transition-all duration-200 ${
                copied
                  ? 'bg-green-500 text-white shadow-lg shadow-green-500/30'
                  : 'bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-400 hover:to-purple-500 text-white shadow-lg shadow-purple-500/30 hover:scale-105 active:scale-95'
              }`}
            >
              {copied ? (
                <>
                  <Check className="w-3.5 h-3.5" /> {t('embed.copied', 'Copiado!')}
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5" /> {t('embed.copy', 'Copiar')}
                </>
              )}
            </button>
          </div>

          {/* Snippet body */}
          <div className="p-3 sm:p-4">
            <textarea
              id={`embed-snippet-${embedSlug}`}
              readOnly
              value={snippet}
              rows={4}
              onFocus={(e) => e.target.select()}
              className="w-full px-3 py-2.5 font-mono text-[11px] sm:text-xs leading-relaxed text-purple-100 bg-black/40 border border-white/10 rounded-lg resize-none focus:ring-2 focus:ring-purple-400 focus:border-transparent transition-colors"
            />
          </div>
        </div>

        {/* Dica */}
        <p className="text-xs text-purple-200/60 text-center mt-4 px-2">
          <Trans i18nKey="embed.tip" t={t}>
            💡 Dica: ajuste o atributo <code className="bg-white/10 px-1.5 py-0.5 rounded text-purple-100 font-mono">height</code> conforme o espaço disponível na sua página.
          </Trans>
        </p>
      </div>
    </section>
  );
}
